import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { Course, Enrollment, UserRole } from '../types';
import { courseService } from '../services/courseService';
import { useAuth } from './AuthContext';
import { useTenant } from './TenantContext';
import { useScope } from './ScopeContext';
import { applyScope } from '../lib/catalogScope';

interface CourseContextType {
  /**
   * Courses for the current viewer, narrowed to their catalogue scope
   * (curriculum) when the tenant configures one.
   *
   * ⚠️ Scoping applies to STUDENTS ONLY. This same list is the instructor and
   * admin course list (`getInstructorCourses`), and filtering it for them
   * would hide courses from the people who wrote them — the same trap the
   * bundle shelf rule documents. Staff always see everything.
   */
  courses: Course[];
  /** The unscoped fetch, for surfaces that must not be narrowed. */
  allCourses: Course[];
  enrollments: Enrollment[];
  searchQuery: string;
  selectedCategory: string;
  isLoading: boolean;
  setSearchQuery: (query: string) => void;
  setSelectedCategory: (category: string) => void;
  enrollInCourse: (courseId: string) => Promise<void>;
  completeLesson: (courseId: string, lessonId: string) => Promise<void>;
  getEnrollment: (courseId: string) => Enrollment | undefined;
  isLessonCompleted: (courseId: string, lessonId: string) => boolean;
  addCourse: (course: Course) => Promise<Course | null>;
  updateCourse: (course: Course) => Promise<void>;
  deleteCourse: (courseId: string) => Promise<void>;
  duplicateCourse: (courseId: string) => Promise<Course | null>;
  refreshData: () => Promise<void>;
}

const CourseContext = createContext<CourseContextType | undefined>(undefined);

export const CourseProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { tenant, tenantId } = useTenant();
  // Read the slug out as a primitive: `tenant` is a fresh object on each
  // resolve, and depending on it directly would churn refreshData's identity
  // and re-run the fetch — the same shape as the auth startup race.
  const tenantSlug = tenant?.slug;
  const [courses, setCourses] = useState<Course[]>([]);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const isInstructorOrAdmin = user?.role === UserRole.INSTRUCTOR || user?.role === UserRole.ADMIN;

  const { axes, selection, isResolving, noteEnrollmentSignal } = useScope();

  // Enrolled courses stay visible whatever the scope. There is deliberately no
  // "show everything" view, so without this exemption switching curriculum
  // would hide a course the student had already paid for and started, and
  // leave them no route back to it.
  const ownedCourseIds = useMemo(
    () => new Set(enrollments.map((e) => e.courseId)),
    [enrollments],
  );

  const scopedCourses = useMemo(
    () => (isInstructorOrAdmin ? courses : applyScope(courses, axes, selection, ownedCourseIds)),
    [courses, axes, selection, ownedCourseIds, isInstructorOrAdmin],
  );

  // Someone with four IEB courses has already told us their curriculum. Offered
  // once, and only when they've made no explicit choice. Offered even when
  // there are NO enrolments: the scope needs to know it has been consulted
  // before it can say the student is undecided (ScopeContext.undecidedAxes).
  useEffect(() => {
    if (isInstructorOrAdmin || isLoading) return;
    if (courses.length === 0) return;
    noteEnrollmentSignal(courses, enrollments);
  }, [courses, enrollments, isLoading, isInstructorOrAdmin, noteEnrollmentSignal]);

  // Initial Data Fetch
  const refreshData = useCallback(async () => {
    if (!user) {
      setCourses([]);
      setEnrollments([]);
      setIsLoading(false);
      return;
    }
    // The Hub is a platform-admin surface, not a learning one — nothing under
    // /hub consumes this context. Fetching there is pure waste, and not cheap:
    // a super-admin has role ADMIN, so it takes the getInstructorCourses path,
    // which pulls every lesson row (content JSONB included) for the tenant.
    if (tenantSlug === 'hub') {
      setCourses([]);
      setEnrollments([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    let fetchedCourses: Course[] = [];
    let fetchedEnrollments: Enrollment[] = [];
    try {
      try {
        fetchedCourses = isInstructorOrAdmin
          ? await courseService.getInstructorCourses(tenantId || undefined)
          : await courseService.getAllCourses(tenantId || undefined);
      } catch (error: any) {
        console.error('CourseContext.refreshData: courses fetch failed', {
          call: isInstructorOrAdmin ? 'getInstructorCourses' : 'getAllCourses',
          message: error?.message,
          code: error?.code,
          details: error?.details,
          hint: error?.hint,
          role: user?.role,
          tenantId,
        });
      }
      if (!isInstructorOrAdmin) {
        try {
          fetchedEnrollments = await courseService.getMyEnrollments();
        } catch (error: any) {
          console.error('CourseContext.refreshData: enrollments fetch failed', {
            call: 'getMyEnrollments',
            message: error?.message,
            code: error?.code,
            details: error?.details,
            hint: error?.hint,
            role: user?.role,
            tenantId,
          });
        }
      }
      setCourses(fetchedCourses);
      setEnrollments(fetchedEnrollments);
    } finally {
      setIsLoading(false);
    }
  }, [user, isInstructorOrAdmin, tenantId, tenantSlug]);

  useEffect(() => {
    refreshData();

    // Listen for payment success events to refresh enrollments
    const handlePaymentSuccess = () => {
      refreshData();
    };

    window.addEventListener('payment-success', handlePaymentSuccess);

    return () => {
      window.removeEventListener('payment-success', handlePaymentSuccess);
    };
  }, [refreshData]);

  const enrollInCourse = async (courseId: string) => {
    if (user?.role === UserRole.INSTRUCTOR || user?.role === UserRole.ADMIN) {
      console.warn('Instructors cannot enroll in courses');
      return;
    }
    try {
      const newEnrollment = await courseService.enrollInCourse(courseId);
      setEnrollments(prev => {
        const exists = prev.some(e => e.courseId === newEnrollment.courseId);
        return exists ? prev : [...prev, newEnrollment];
      });
    } catch (error) {
      console.error(error);
    }
  };

  const completeLesson = async (courseId: string, lessonId: string) => {
    try {
      const prevProgress = enrollments.find(e => e.courseId === courseId)?.progress ?? 0;
      const updatedEnrollment = await courseService.completeLesson(courseId, lessonId);
      setEnrollments(prev => prev.map(e => e.courseId === courseId ? updatedEnrollment : e));

      // Dispatch gamification event for lesson completion
      window.dispatchEvent(new CustomEvent('gamification:lesson-complete', {
        detail: {
          courseId,
          lessonId,
          completedLessonCount: updatedEnrollment.completedLessonIds?.length ?? 0,
          progress: updatedEnrollment.progress,
        }
      }));

      // On the 0->100 transition, fire course-complete so gamification can award
      // course-count badges (Graduate/Scholar) and this course's completion badge.
      if (prevProgress < 100 && updatedEnrollment.progress === 100) {
        const updatedEnrollments = enrollments.map(e =>
          e.courseId === courseId ? updatedEnrollment : e
        );
        const completedCourseCount = updatedEnrollments.filter(e => e.progress === 100).length;
        const totalPoints = updatedEnrollments
          .filter(e => e.progress === 100)
          .reduce((sum, e) => sum + (courses.find(c => c.id === e.courseId)?.points ?? 0), 0);

        window.dispatchEvent(new CustomEvent('gamification:course-complete', {
          detail: { courseId, completedCourseCount, totalPoints }
        }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getEnrollment = (courseId: string) => {
    return enrollments.find(e => e.courseId === courseId);
  };

  const isLessonCompleted = (courseId: string, lessonId: string) => {
    const enrollment = enrollments.find(e => e.courseId === courseId);
    return enrollment ? enrollment.completedLessonIds.includes(lessonId) : false;
  };

  const addCourse = async (course: Course): Promise<Course | null> => {
    try {
      const newCourse = await courseService.createCourse(course);
      setCourses(prev => [...prev, newCourse]);
      return newCourse;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const updateCourse = async (updatedCourse: Course) => {
    try {
      const result = await courseService.updateCourse(updatedCourse);
      setCourses(prev => prev.map(c => c.id === result.id ? result : c));
    } catch (error) {
      console.error(error);
    }
  };

  // Listen for course updates from other parts of the app (e.g. course builder)
  useEffect(() => {
    const handler = (e: CustomEvent<Course>) => {
      setCourses(prev => prev.map(c => c.id === e.detail.id ? e.detail : c));
    };
    window.addEventListener('course:updated', handler as EventListener);
    return () => window.removeEventListener('course:updated', handler as EventListener);
  }, []);

  const deleteCourse = async (courseId: string) => {
    await courseService.deleteCourse(courseId);
    setCourses(prev => prev.filter(c => c.id !== courseId));
  };

  const duplicateCourse = async (courseId: string): Promise<Course | null> => {
    try {
      const newCourse = await courseService.duplicateCourse(courseId);
      await refreshData();
      return newCourse;
    } catch (error) {
      console.error('Failed to duplicate course:', error);
      return null;
    }
  };

  return (
    <CourseContext.Provider value={{
      courses: scopedCourses,
      allCourses: courses,
      enrollments,
      searchQuery,
      selectedCategory,
      // Students wait for the scope to resolve. Until tag categories land,
      // `axes` is empty and applyScope is a no-op — so every surface would
      // render the FULL unscoped catalogue (both curricula interleaved) and
      // then snap to the scoped one. That's the flash useTagCategories' cache
      // was written to stop for the grouped shelves, one layer up. Staff don't
      // wait: the scope never applies to them, so it would be pure delay.
      isLoading: isLoading || (!isInstructorOrAdmin && isResolving),
      setSearchQuery,
      setSelectedCategory,
      enrollInCourse, 
      completeLesson,
      getEnrollment,
      isLessonCompleted,
      addCourse,
      updateCourse,
      deleteCourse,
      duplicateCourse,
      refreshData
    }}>
      {children}
    </CourseContext.Provider>
  );
};

export const useCourse = () => {
  const context = useContext(CourseContext);
  if (context === undefined) {
    throw new Error('useCourse must be used within a CourseProvider');
  }
  return context;
};